import { cloneDeep, pick } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { getMaterialAssets } from '@/i18n/assets';
import { LayerType, TextAlignType, type Cell, type CellStyle } from '@/version-types';
import type { TablePreset } from '.';
import presetImage from './assets/preset8.png';

const PRESET_ID = '653739d2';

const allStyleWithoutFontSize: CellStyle = {
    bold: true,
    color: '#000000ff',
    textAlign: TextAlignType.CENTER,
    border: {
        top: {
            color: '#4a4e58ff',
            style: 'line-0',
            width: 2,
        },
        right: {
            color: '#4a4e58ff',
            style: 'line-0',
            width: 2,
        },
        bottom: {
            color: '#4a4e58ff',
            style: 'line-0',
            width: 2,
        },
        left: {
            color: '#4a4e58ff',
            style: 'line-0',
            width: 2,
        },
    },
};

const getTableByPreset: TablePreset['create'] = () => {
    return {
        id: uuidv4(),
        layerType: LayerType.TABLE,
        width: 869,
        height: 521,
        rotate: 0,
        opacity: 1,
        title: '',
        fitWidth: 0,
        fitHeight: 0,
        materialIds: {},
        extraInfo: {
            lockRatio: false,
        },
        data: {
            presetId: PRESET_ID,
            screenshotUrl: getMaterialAssets().tableLayer.screenshotUrl.table8,
            cells: [
                [
                    {
                        hIndex: 0,
                        vIndex: 0,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: [],
                    },
                    {
                        hIndex: 1,
                        vIndex: 0,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: [],
                    },
                    {
                        hIndex: 2,
                        vIndex: 0,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: [],
                    },
                    {
                        hIndex: 3,
                        vIndex: 0,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: [],
                    },
                ],
                [
                    {
                        hIndex: 0,
                        vIndex: 1,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: [],
                    },
                    {
                        hIndex: 1,
                        vIndex: 1,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: [],
                    },
                    {
                        hIndex: 2,
                        vIndex: 1,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: [],
                    },
                    {
                        hIndex: 3,
                        vIndex: 1,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: [],
                    },
                ],
                [
                    {
                        hIndex: 0,
                        vIndex: 2,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: [],
                    },
                    {
                        hIndex: 1,
                        vIndex: 2,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: [],
                    },
                    {
                        hIndex: 2,
                        vIndex: 2,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: [],
                    },
                    {
                        hIndex: 3,
                        vIndex: 2,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: [],
                    },
                ],
                [
                    {
                        hIndex: 0,
                        vIndex: 3,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: [],
                    },
                    {
                        hIndex: 1,
                        vIndex: 3,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: [],
                    },
                    {
                        hIndex: 2,
                        vIndex: 3,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: [],
                    },
                    {
                        hIndex: 3,
                        vIndex: 3,
                        colSpan: 1,
                        rowSpan: 1,
                        content: '',
                        styleIds: [],
                    },
                ],
            ],
            baseCells: {
                horizontal: [216.75, 216.75, 216.75, 216.75],
                vertical: [129.75, 129.75, 129.75, 129.75],
            },
            allStyleId: '1',
            styles: {
                '1': {
                    ...allStyleWithoutFontSize,
                    fontSize: 30,
                },
            },
        },
        lock: false,
        children: [],
        shine: true,
        hidden: false,
    };
};

const tablePreset: TablePreset = {
    id: PRESET_ID,
    preview: presetImage,
    create: getTableByPreset,
    getCellDefaultStyle: (cell, layerDaya) => {
        // 初识时确定的fontSize存在allStyleId里面(allStyleId里的fontSize会因缩放而改变), 这里拿出来用
        const fontSize = layerDaya.styles[layerDaya.allStyleId].fontSize!;
        return {
            ...allStyleWithoutFontSize,
            fontSize,
            backgroundColor: cell.vIndex % 2 === 0 ? '#ebecf0ff' : '#ffffffff',
        };
    },
    overwriteStyle: (oldTableLayer) => {
        const newTableLayer = cloneDeep(oldTableLayer);

        const newStyleId1 = `${Math.max(0, ...Object.keys(newTableLayer.data.styles).map(Number)) + 1}`;
        const newStyleId2 = `${+newStyleId1 + 1}`;
        const cells = newTableLayer.data.cells.flat().filter(Boolean) as Cell[];
        cells.forEach((c) => {
            c.styleIds.push(c.vIndex % 2 === 0 ? newStyleId1 : newStyleId2);
        });
        const style: CellStyle = pick(allStyleWithoutFontSize, ['border', 'backgroundColor']);
        newTableLayer.data.styles[newStyleId1] = {
            ...style,
            backgroundColor: '#ebecf0ff',
        };
        newTableLayer.data.styles[newStyleId2] = {
            ...style,
            backgroundColor: '#ffffffff',
        };

        return newTableLayer;
    },
};

export default tablePreset;
